import React from "react";
import { useParams, Link } from "react-router-dom";
import { services } from "../servicesData";

const ServiceDetail = () => {
  const { slug } = useParams();

  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f2ea] px-6">
        <p className="text-gray">
          Service not found. Go back{" "}
          <Link to="/" className="text-primary underline">
            home
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f2ea]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#efe3d2] to-[#f7f2ea]" />
        <div className="absolute -top-24 -right-24 h-72 w-72 bg-white/40 rounded-full blur-3xl" />
        <div className="absolute top-10 -left-20 h-64 w-64 bg-accent/20 rounded-full blur-3xl" />

        <div className="relative container mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-20">
          {/* breadcrumb */}
          <div className="text-xs uppercase tracking-widest text-gray mb-3">
            <Link to="/" className="hover:text-primary">
              Home
            </Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-primary">{service.title}</span>
          </div>

          {/* icon + title */}
          <div className="flex items-center gap-4 mb-4">
            {service.icon}
            <h1 className="text-3xl lg:text-5xl font-extrabold text-primary leading-tight">
              {service.title}
            </h1>
          </div>

          {/* blurb */}
          <p className="mt-2 max-w-2xl text-base lg:text-lg text-gray leading-relaxed">
            {service.blurb}
          </p>

          {/* quick highlight chips (first 3 bullets) */}
          <div className="mt-8 flex flex-wrap gap-3">
            {service.bullets?.slice(0, 3).map((b, i) => (
              <div
                key={i}
                className="bg-white/80 backdrop-blur px-4 py-2 rounded-full text-sm text-primary shadow-sm border border-white"
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT CARD */}
      <section id="services" className="container mx-auto px-6 -mt-18 pb-30">
        <div className="bg-white rounded-2xl shadow-xl border border-accent/20 p-6 lg:p-10">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* MAIN CONTENT */}
            <div className="lg:col-span-2 space-y-10">
              {/* What's Included */}
              <div>
                <SectionTitle title="What’s Included" />
                <ul className="grid sm:grid-cols-2 gap-3 mt-4">
                  {service.bullets?.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 bg-[#fbf7f1] rounded-xl p-4"
                    >
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-accent flex-shrink-0" />
                      <p className="text-sm lg:text-base text-primary">
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              {service.pricing?.length > 0 && (
                <div>
                  <SectionTitle title="Pricing" />
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {service.pricing.map((p, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-accent/20 bg-white shadow-sm p-5 hover:shadow-md transition"
                      >
                        <h3 className="text-lg font-semibold text-primary">
                          {p.name}
                        </h3>
                        <div className="mt-1 text-accent font-bold text-xl">
                          {p.price}
                        </div>
                        <p className="mt-2 text-sm text-gray leading-relaxed">
                          {p.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SIDEBAR */}
            <aside className="space-y-6">
              {/* CTA Panel */}
              <div className="bg-primary text-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-2">
                  Ready to get started?
                </h3>

                <p className="text-sm text-white/90 leading-relaxed">
                  Book a consultation to discuss your goals, timeline, and the
                  best legal strategy for your situation.
                </p>

                <a
                  href="https://calendar.app.google/BBXVdwFStqB3HFEL7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-md transition-all duration-300 mt-6 w-full text-center"
                >
                  Schedule a Consultation
                </a>
              </div>

              {/* Note */}
              <div className="bg-[#fbf7f1] rounded-2xl p-6 border border-accent/20">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-primary mb-2">
                  Note
                </h3>
                <p className="text-sm text-gray leading-relaxed">
                  This page is for general information only and does not create
                  an attorney client relationship.
                </p>
              </div>

              {/* Back to services */}
              <div className="mb-8">
                <Link
                  to="/services"
                  className="inline-flex items-center text-primary hover:text-accent font-semibold transition-colors duration-200"
                >
                  <span className="mr-2">←</span>
                  Back to Services
                </Link>
              </div>
              {/* Back to home */}
              <div className="mb-6">
                <Link
                  to="/"
                  className="inline-flex items-center text-primary hover:text-accent font-semibold transition-colors duration-200"
                >
                  ← Back to Home
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

const SectionTitle = ({ title }) => (
  <div className="flex items-center gap-3">
    <div className="h-6 w-1.5 rounded-full bg-accent" />
    <h2 className="text-xl lg:text-2xl font-bold text-primary">{title}</h2>
  </div>
);

export default ServiceDetail;
